import { X } from "lucide-react";
import type * as React from "react";
import { Eyebrow } from "@/components/shared/Eyebrow";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface PanelShellProps {
  eyebrow: string;
  title: string;
  subtitle?: string;
  label: string;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * The month detail panel. It sits beside the grid rather than over it, so the grid
 * keeps its scroll position and the RM keeps their place. Escape closes it.
 */
export function PanelShell({
  eyebrow,
  title,
  subtitle,
  label,
  onClose,
  children,
}: PanelShellProps) {
  return (
    <aside
      aria-label={label}
      className="flex min-w-0 flex-[1_1_340px] flex-col gap-4 rounded-lg border bg-card p-5 shadow-sm lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:max-w-[420px] lg:overflow-y-auto"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <Eyebrow>{eyebrow}</Eyebrow>
          <h2 className="font-display text-xl font-semibold text-heading">{title}</h2>
          {subtitle ? <span className="text-xs text-muted-foreground">{subtitle}</span> : null}
        </div>
        <Button variant="outline" size="icon" aria-label="Close month detail" onClick={onClose}>
          <X />
        </Button>
      </div>
      {children}
    </aside>
  );
}

export function PanelLabel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "font-mono text-xs font-medium tracking-wider text-muted-foreground uppercase",
        className,
      )}
    >
      {children}
    </span>
  );
}

interface StatProps {
  label: string;
  value: string;
  sub?: string;
  tone?: "over" | "free";
}

export function Stat({ label, value, sub, tone }: StatProps) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <PanelLabel>{label}</PanelLabel>
      <span
        className={cn(
          "font-display text-xl font-semibold text-heading",
          tone === "over" && "text-[color:var(--avail-over)]",
          tone === "free" && "text-[color:var(--avail-some-room-fg)]",
        )}
      >
        {value}
      </span>
      {sub ? <span className="text-xs text-muted-foreground">{sub}</span> : null}
    </div>
  );
}

/** Free (blue, left) and over (red-orange, right) on one centred bar, as a share of Available Hours. */
export function SplitBar({
  netHours,
  availableHours,
}: {
  netHours: number;
  availableHours: number | null;
}) {
  const scale = availableHours !== null && availableHours > 0 ? availableHours : 1;
  const free = Math.min(Math.max(netHours, 0) / scale, 1) * 50;
  const over = Math.min(Math.max(-netHours, 0) / scale, 1) * 50;
  return (
    <div className="flex flex-col gap-1.5">
      <div aria-hidden className="relative h-3 rounded-sm bg-muted">
        <span
          className="absolute inset-y-0 right-1/2 rounded-l-sm bg-[var(--avail-free-bar)]"
          style={{ width: `${free}%` }}
        />
        <span
          className="absolute inset-y-0 left-1/2 rounded-r-sm bg-[var(--avail-over)]"
          style={{ width: `${over}%` }}
        />
        <span className="absolute -inset-y-0.5 left-1/2 -ml-px w-0.5 bg-heading" />
      </div>
      <div className="flex justify-between font-mono text-xs tracking-wider text-muted-foreground">
        <span>FREE</span>
        <span>AVAILABLE HOURS</span>
        <span>OVER</span>
      </div>
    </div>
  );
}

/** A tinted note box: warning (data problem) or info (a free month). */
export function Note({
  tone,
  children,
}: {
  tone: "warning" | "info" | "neutral";
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-md px-3 py-2.5 text-xs leading-relaxed",
        tone === "warning" &&
          "bg-[color-mix(in_oklab,var(--avail-issue)_14%,var(--card))] text-heading",
        tone === "info" && "bg-[var(--avail-some-room)] text-[color:var(--avail-some-room-fg)]",
        tone === "neutral" && "bg-muted text-heading",
      )}
    >
      {children}
    </div>
  );
}
